import { ExportFormatsEnum } from 'APIPackage/ImportExportModule/enums/ExportFormatsEnum';
import { IKnmPlan } from 'PlanListPackage/MainModule/interfaces/IKnmPlan';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { IKnmAttachment } from 'APIPackage/KnmModule/interfaces/IKnmAttachment';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { KnmVersionsEnum } from 'APIPackage/KnmModule/enums/KnmVersionsEnum';
import { IKnmHistoryItem } from 'APIPackage/KnmModule/interfaces/IKnmHistoryItem';
import { IFilterResult } from 'APIPackage/MainModule/interfaces/IFilterResult';
import { getServiceBaseURL } from 'APIPackage/MainModule/common/getServiceBaseURL';
import { APIServiceEnum } from 'APIPackage/MainModule/enums/APIServiceEnum';
import { getLawInterfaceFromStorage } from 'CorePackage/MainModule/common/getLawInterfaceFromStorage';
import { ISign } from 'APIPackage/KnmModule/interfaces/ISign';
import { apiDelete, apiGet, apiPost, apiPut } from 'APIPackage/MainModule/common/apiTools';

import { ErknmPlanStatusesEnum } from '../enums/ErknmPlanStatusesEnum';
import { IKnmPlanResponse } from '../interfaces/IKnmPlanResponse';
import { IKnmPlanFilterRaw } from '../interfaces/IKnmPlanFilterRaw';
import { INewKnmPlan } from '../interfaces/INewKnmPlan';
import { IKnmPlanRaw } from '../interfaces/IKnmPlanRaw';
import { IExport } from '../../ImportExportModule/interfaces/IExport';
import { PlanStatusEnum } from '../enums/PlanStatusEnum';
import { IPlanStatusUpdate } from '../interfaces/IPlanStatusUpdate';
import { IKnmPlanInfo } from '../interfaces/IKnmPlanInfo';
import { IConsiderPlan } from '../interfaces/IConsiderPlan';

export async function createPlan(data: INewKnmPlan, isFederalLaw248: boolean): Promise<IKnmPlanRaw> {
  const params = new URLSearchParams();

  params.set('isFederalLaw248', String(isFederalLaw248));

  return apiPost<IKnmPlanRaw>({
    url: `${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm-plan?${params}`,
    body: data,
  });
}

export async function updatePlanById(
  id: string,
  data: Partial<IKnmPlan> & {
    ervkProsecutorOrgUuid: string;
    ervkProsecutorOrgVersionUuid: string;
    prosecutorOrgName: string;
  },
): Promise<IKnmPlanRaw> {
  return apiPut<IKnmPlanRaw>(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm-plan/${id}`, data);
}

export async function updatePlanStatusById(id: number, status: PlanStatusEnum): Promise<IKnmPlanRaw> {
  return apiPut<IKnmPlanRaw>(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm-plan/status/${id}?status=${status}`);
}

export async function deletePlan(id: number): Promise<void> {
  return apiDelete(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm-plan/${id}`);
}

export async function fetchPlans(
  filter: IKnmPlanFilterRaw | null,
  page = 0,
  order = '',
  size = 50,
  isErp?: boolean,
): Promise<IKnmPlanResponse> {
  const lawInterface = getLawInterfaceFromStorage();
  let version = lawInterface === LawInterfacesEnum.FZ_248 ? KnmVersionsEnum.ERKNM : KnmVersionsEnum.ERP;

  if (typeof isErp === 'boolean') {
    version = isErp ? KnmVersionsEnum.ERP : KnmVersionsEnum.ERKNM;
  }

  const params = new URLSearchParams();

  params.set('page', String(page));
  params.set('size', String(size));
  order && params.set('order', order);

  return apiPost<IKnmPlanResponse>({
    url: `${getServiceBaseURL(APIServiceEnum.INDEX_SERVICE, isErp)}/knm-plan/${
      version === KnmVersionsEnum.ERKNM ? 'all' : 'find-indexes'
    }?${params}`,
    body: {
      ...filter,
      version,
    },
  });
}

export async function fetchPlanById(id: IKnm['planId']): Promise<IKnmPlan> {
  return apiGet<IKnmPlan>(`${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/knm-plan/${id}`);
}

export async function fetchPlanSignatureUpload(id: IKnm['planId'], document: File, sign: File) {
  const formData = new FormData();

  formData.append('planSignature', sign);
  formData.append('planFile', document);

  return apiPost({
    url: `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/signature/knm-plan/${id}/upload`,
    body: formData,
  });
}

export async function fetchPlanChangeKno(id: IKnm['planId'], knoId: string, attachment: IKnmAttachment) {
  return apiPost({
    url: `${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm-plan/${id}/kno/${knoId}`,
    body: attachment,
  });
}

export async function fetchPlanInfo(id: string): Promise<IKnmPlanInfo> {
  return apiGet<IKnmPlanInfo>(`${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/knm-plan/${id}/info`);
}

export async function exportPlan(
  format: ExportFormatsEnum,
  id: number | string,
  approved: boolean,
  considered: boolean,
): Promise<IExport> {
  const lawInterface = getLawInterfaceFromStorage();
  const params = new URLSearchParams();

  params.set('approved', String(approved));
  params.set('considered', String(considered));
  params.set('format', format);
  params.set('isFederalLaw248', String(lawInterface === LawInterfacesEnum.FZ_248));

  return apiPost<IExport>({
    url: `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/knm-plan/export/${id}?${params}`,
    body: { id },
  });
}

export async function exportPlans(format: ExportFormatsEnum, filter: IKnmPlanFilterRaw): Promise<IExport> {
  const lawInterface = getLawInterfaceFromStorage();
  const params = new URLSearchParams();

  params.set('format', format);
  params.set('isFederalLaw248', String(lawInterface === LawInterfacesEnum.FZ_248));

  return apiPost<IExport>({
    url: `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/knm-plan/export?${params}`,
    body: filter,
  });
}

export async function exportDocumentsForKnoByPlanId(id: number): Promise<IExport> {
  const lawInterface = getLawInterfaceFromStorage();
  const params = new URLSearchParams();

  params.set('isFederalLaw248', String(lawInterface === LawInterfacesEnum.FZ_248));

  return apiPost<IExport>({
    url: `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/knm-plan/export/${id}/documents-for-kno?${params}`,
  });
}

export async function fetchPlanFilters(): Promise<IFilterResult[]> {
  const lawInterface = getLawInterfaceFromStorage();
  const params = new URLSearchParams();
  const version = lawInterface === LawInterfacesEnum.FZ_248 ? KnmVersionsEnum.ERKNM : KnmVersionsEnum.ERP;

  params.set('version', String(version));

  return apiGet<IFilterResult[]>(`${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/filters/knm-plan?${params}`);
}

export async function fetchPlanFilterById(id: IFilterResult['id']): Promise<IKnmPlanFilterRaw> {
  return apiGet<IKnmPlanFilterRaw>(`${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/filters/knm-plan/${id}`);
}

export async function createPlanFilter(filter: IKnmPlanFilterRaw): Promise<IFilterResult> {
  const lawInterface = getLawInterfaceFromStorage();
  const version = lawInterface === LawInterfacesEnum.FZ_248 ? KnmVersionsEnum.ERKNM : KnmVersionsEnum.ERP;
  const planFilter = { ...filter, version };

  return apiPost<IFilterResult>({
    url: `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/filters/knm-plan`,
    body: planFilter,
  });
}

export async function updatePlanFilter(id: IFilterResult['id'], filter: IKnmPlanFilterRaw): Promise<IKnmPlanFilterRaw> {
  return apiPut<IKnmPlanFilterRaw>(`${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/filters/knm-plan/${id}`, filter);
}

export async function deletePlanFilter(id: IFilterResult['id']): Promise<void> {
  return apiDelete(`${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/filters/${id}`);
}

export async function getPlanStatuses(
  isFederalLaw248: boolean,
): Promise<Array<ErknmPlanStatusesEnum | PlanStatusEnum>> {
  const params = new URLSearchParams();

  params.append('isFederalLaw248', String(isFederalLaw248));

  return apiGet<Array<ErknmPlanStatusesEnum | PlanStatusEnum>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/knm-plan/statuses?${params}`,
  );
}

export async function updateErknmPlanStatusById(id: number, params: IPlanStatusUpdate): Promise<void> {
  return apiPut(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm-plan/erknm-status/${id}`, params);
}

export async function updatePlanStatusToConsiderById(id: number, params: IConsiderPlan): Promise<void> {
  return apiPut(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm-plan/${id}/consider`, params);
}

export async function getPlanXlsxById(id: number): Promise<Blob> {
  return apiGet<Blob>(`${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/knm-plan/${id}/xlsx`);
}

export async function getPlanXmlById(id: number): Promise<Blob> {
  return apiGet<Blob>(`${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/knm-plan/${id}/xml`);
}

export async function signPlanXlsxById(id: number, params: IKnmAttachment): Promise<IKnmPlan> {
  return apiPut<IKnmPlan>(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm-plan/sign-plan-xlsx/${id}`, params);
}

export interface ISignPlanError {
  errorMessage?: string;
}

export async function fetchSignPlan(data: ISign): Promise<ISignPlanError> {
  const { id, document, file, signature, fileName } = data;
  const formData = new FormData();

  formData.append('file', file);
  formData.append('document', document);
  formData.append('signature', signature);
  formData.append('fileName', fileName);

  return apiPost<ISignPlanError>({
    url: `${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm-plan/sign-plan/${id}`,
    body: formData,
  });
}

export async function fetchSignTestPlan(planId: number): Promise<boolean> {
  return apiPost<boolean>({
    url: `${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm-plan/sign-test-plan/${planId}`,
  });
}

export async function fetchPlanHistory(planId: IKnmPlan['id']): Promise<IKnmHistoryItem[]> {
  return apiGet<IKnmHistoryItem[]>(`${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/knm-plan/${planId}/history`);
}

export async function getApprovedPlansForCurrentYear(
  frguProsecutorOrgId: number | string | null,
  ervkProsecutorOrgId: string | null,
  knoId: number | string | null,
  kindKontrolId: string,
): Promise<number[]> {
  const params = new URLSearchParams();

  if (ervkProsecutorOrgId) {
    params.append('ervkProsecutorOrgId', ervkProsecutorOrgId);
  }

  if (!ervkProsecutorOrgId && frguProsecutorOrgId) {
    params.append('frguProsecutorOrgId', String(frguProsecutorOrgId));
  }

  params.append('knoId', String(knoId));
  params.append('supervisionTypeUuid', kindKontrolId);

  return apiGet<number[]>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/knm-plan/get-approved-plans-for-current-year?${params}`,
  );
}
